/**
 * Where a batch run's artefacts live in S3.
 *
 * A grouping's *definition* is a JSON file in the project's Automation Scripts
 * folder, beside the scripts it references. Its *run output* is not — every
 * batch artefact sits under the project's `groupings/` root:
 *
 *   s3://{bucket}/projects/{projectId}/{projectName}/groupings/{groupingId}/
 *     runs/run{N}/
 *       manifest.json
 *       reports/consolidated-report.html
 *       summary/consolidated-summary.json
 *
 * `{groupingId}` is the stable `grp-xxxxxxxx` id, never the file name — a
 * rename moves the definition file but must not orphan the run history.
 *
 * **These address the folders, not the files inside them.** The file download
 * endpoint takes a folder and resolves its contents; handing it a full file
 * path returns a 404. That is how the single-run history reads a summary, and
 * this matches it.
 *
 * **They are also a last resort, not the normal way in.** The batch listing
 * returns each run's `reportFolder`, and the history uses that. The only
 * caller here is the live tracker, which wants the summary the moment a batch
 * ends rather than waiting on the listing.
 *
 * @module shared/middleware/config/groupingPaths
 */

/** `projects/{projectId}/{projectName}/groupings/{groupingId}/runs/run{N}` */
export const groupingRunPrefix = (projectId, projectName, groupingId, batchRunNumber) => {
  if (!projectId || !projectName || !groupingId || batchRunNumber == null) return null;
  return `projects/${projectId}/${projectName}/groupings/${groupingId}/runs/run${batchRunNumber}`;
};

/** Folder holding the batch's figures, written when the last member ends. */
export const groupingSummaryFolder = (projectId, projectName, groupingId, batchRunNumber) => {
  const prefix = groupingRunPrefix(projectId, projectName, groupingId, batchRunNumber);
  return prefix ? `${prefix}/summary` : null;
};

/** Folder holding the batch's HTML report. */
export const groupingReportFolder = (projectId, projectName, groupingId, batchRunNumber) => {
  const prefix = groupingRunPrefix(projectId, projectName, groupingId, batchRunNumber);
  return prefix ? `${prefix}/reports` : null;
};
